import {
  Box,
  Divider,
  Skeleton,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import type { ReactElement } from "react";
import ClientAccessPanel from "components/accessGroups/ClientAccessPanel";
import ReadOnlyTooltip from "components/common/ReadOnlyTooltip";
import {
  switchTree,
  type ClientSwitchState,
  type SwitchLocks,
} from "components/accessGroups/clientPanelModel";
import type {
  AccessCatalog,
  ClientSwitch,
  ClientSwitchField,
} from "types/interfaces/access";
import { ADMIN_ONLY_HINT } from "./lockHints";
import StepHeading from "./StepHeading";

interface Props {
  catalog: AccessCatalog | undefined;
  state: ClientSwitchState;
  onSwitch: (field: ClientSwitchField, on: boolean) => void;
  onChoice: (field: "integrations_experience", value: string) => void;
  canEdit: boolean;
  /** Said over every switch while the user may not change here; null says
   * nothing. */
  lockedHint?: string | null;
  /** What stays as it is for this editor: the pages their group does not open. */
  locks?: SwitchLocks;
}

/** A disabled control swallows the pointer, so the hint hangs off a box around
 * it. */
function Hint({
  title,
  children,
}: {
  title: string | null;
  children: ReactElement;
}) {
  if (!title) return children;
  return (
    <Tooltip title={title} placement="top" describeChild>
      <Box sx={{ width: "100%" }}>{children}</Box>
    </Tooltip>
  );
}

function SwitchRow({
  item,
  checked,
  disabled,
  onChange,
  nested = false,
  muted = false,
  hint = null,
}: {
  item: ClientSwitch;
  checked: boolean;
  disabled: boolean;
  onChange: (on: boolean) => void;
  nested?: boolean;
  /** Listed for reference: the editor's group does not open its page. */
  muted?: boolean;
  /** Why the switch may not change, said over it. */
  hint?: string | null;
}) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: nested ? 14 : undefined,
            color: muted ? "text.disabled" : undefined,
          }}
        >
          {item.name}
        </Typography>
        <Typography
          variant="caption"
          color={muted ? "text.disabled" : "text.secondary"}
          display="block"
          sx={{ lineHeight: 1.4 }}
        >
          {item.description}
        </Typography>
      </Box>
      <ReadOnlyTooltip
        active={disabled && Boolean(hint)}
        title={hint ?? undefined}
      >
        <Switch
          checked={checked}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
          inputProps={{ "aria-label": item.name }}
          size={nested ? "small" : "medium"}
        />
      </ReadOnlyTooltip>
    </Box>
  );
}

/** Step 2 of the client user form: the switches that are a client user's whole
 * access, each with what it opens, next to what they add up to. */
export default function ClientUserAccessStep({
  catalog,
  state,
  onSwitch,
  onChoice,
  canEdit,
  lockedHint = null,
  locks,
}: Props) {
  // Off for the editor's rights: the whole form, or a page their group does not
  // open. A sub-option off while its switch is says nothing.
  const hintFor = (locked: boolean) =>
    !canEdit ? lockedHint : locked ? ADMIN_ONLY_HINT : null;
  return (
    <Box>
      <StepHeading
        title="User Access"
        subtitle="Turn on the pages this client user opens. Client users have no access group: these switches are their whole access."
      />
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            md: "minmax(0, 1fr) minmax(0, 1fr)",
          },
          gap: 3,
          alignItems: "start",
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {!catalog
            ? Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} variant="rounded" height={72} />
              ))
            : switchTree(catalog).map(({ item, children }) => {
                const on = state[item.field] === true;
                const locked = locks?.switches.has(item.field) ?? false;
                return (
                  <Box
                    key={item.field}
                    sx={{
                      border: "1px solid",
                      borderColor: on ? "primary.main" : "divider",
                      borderRadius: 1,
                      bgcolor: canEdit && locked ? "grey.100" : "transparent",
                      p: 2,
                      display: "flex",
                      flexDirection: "column",
                      gap: 1.5,
                    }}
                  >
                    <SwitchRow
                      item={item}
                      checked={on}
                      disabled={!canEdit || locked}
                      muted={canEdit && locked}
                      hint={hintFor(locked)}
                      onChange={(next) => onSwitch(item.field, next)}
                    />
                    {item.choice_field && (
                      <Hint
                        title={
                          on
                            ? hintFor(locks?.choices.has(item.field) ?? false)
                            : null
                        }
                      >
                        <ToggleButtonGroup
                          size="small"
                          exclusive
                          fullWidth
                          value={
                            state[item.choice_field] ??
                            item.choices[0]?.value ??
                            null
                          }
                          onChange={(_, next: string | null) => {
                            if (next && item.choice_field) {
                              onChoice(item.choice_field, next);
                            }
                          }}
                          disabled={
                            !canEdit ||
                            !on ||
                            (locks?.choices.has(item.field) ?? false)
                          }
                          aria-label={`${item.name} experience`}
                        >
                          {item.choices.map((choice) => (
                            <ToggleButton
                              key={choice.value}
                              value={choice.value}
                              sx={{ textTransform: "none", fontWeight: 600 }}
                            >
                              {choice.label}
                            </ToggleButton>
                          ))}
                        </ToggleButtonGroup>
                      </Hint>
                    )}
                    {children.map((child) => (
                      <Box key={child.field}>
                        <Divider sx={{ mb: 1.5 }} />
                        <SwitchRow
                          item={child}
                          nested
                          checked={state[child.field] === true}
                          disabled={
                            !canEdit ||
                            !on ||
                            (locks?.switches.has(child.field) ?? false)
                          }
                          muted={
                            canEdit &&
                            (locks?.switches.has(child.field) ?? false)
                          }
                          hint={
                            on
                              ? hintFor(
                                  locks?.switches.has(child.field) ?? false,
                                )
                              : null
                          }
                          onChange={(next) => onSwitch(child.field, next)}
                        />
                      </Box>
                    ))}
                  </Box>
                );
              })}
        </Box>
        {catalog ? (
          <ClientAccessPanel
            catalog={catalog}
            state={state}
            subHeader="What this user will be able to see and do."
          />
        ) : (
          <Skeleton variant="rounded" height={220} />
        )}
      </Box>
    </Box>
  );
}
